import {useEffect, useRef, useState} from 'react'
import {IconX} from '@tabler/icons-react'
import {useApiToken} from './api/useApiToken'
import Overlay from './Overlay'

interface Props {
  media: string
  /** прежняя область в процентах [x1, y1, x2, y2] или null */
  rect: number[] | null
  onDone: (rect: number[] | null) => void
  onClose: () => void
}

const clamp = (v: number) => Math.max(0, Math.min(100, v))

/**
 * Выбор области снимка под аватарку.
 *
 * Область квадратная: аватарка везде квадратная, и прямоугольный выбор
 * всё равно обрезался бы по центру — лучше показать сразу то, что будет.
 *
 * Координаты в процентах, а не в точках: так их хранит сам Gramps
 * (MediaRef.rect), поэтому выбранное лицо видно и в штатном интерфейсе,
 * и переживает замену файла на скан большего размера.
 */
export default function CropPicker({media, rect, onDone, onClose}: Props) {
  const jwt = useApiToken()
  const boxRef = useRef<HTMLDivElement>(null)
  const [sel, setSel] = useState<number[]>(rect && rect.length === 4 ? rect : [25, 25, 75, 75])
  const [drag, setDrag] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  /** Точка в процентах от показанного снимка. */
  const at = (e: {clientX: number; clientY: number}) => {
    const r = boxRef.current!.getBoundingClientRect()
    return {x: clamp(((e.clientX - r.left) / r.width) * 100), y: clamp(((e.clientY - r.top) / r.height) * 100)}
  }

  /* Квадрат считаем в точках, а не в процентах: у неквадратного снимка
     равные проценты по осям дают вытянутый прямоугольник. */
  const square = (ax: number, ay: number, bx: number, by: number): number[] => {
    const r = boxRef.current!.getBoundingClientRect()
    const dx = ((bx - ax) / 100) * r.width
    const dy = ((by - ay) / 100) * r.height
    const side = Math.min(Math.abs(dx), Math.abs(dy))
    const sx = (side / r.width) * 100 * Math.sign(dx || 1)
    const sy = (side / r.height) * 100 * Math.sign(dy || 1)
    const x1 = clamp(Math.min(ax, ax + sx))
    const y1 = clamp(Math.min(ay, ay + sy))
    return [x1, y1, clamp(x1 + Math.abs(sx)), clamp(y1 + Math.abs(sy))]
  }

  const start = useRef<{x: number; y: number} | null>(null)

  const onDown = (e: React.PointerEvent) => {
    const p = at(e)
    start.current = p
    setDrag(true)
    boxRef.current?.setPointerCapture(e.pointerId)
    setSel([p.x, p.y, p.x, p.y])
  }
  const onMove = (e: React.PointerEvent) => {
    if (!start.current) return
    const p = at(e)
    setSel(square(start.current.x, start.current.y, p.x, p.y))
  }
  const onUp = () => { start.current = null; setDrag(false) }

  const [x1, y1, x2, y2] = sel
  const tiny = x2 - x1 < 3 || y2 - y1 < 3
  const src = `/api/media/${media}/thumbnail/1400?jwt=${jwt}`

  return (
    <Overlay>
      <div className="modal-bg" onClick={onClose}>
        <div className="modal wide" onClick={e => e.stopPropagation()}>
          <div className="mhead">
            <h2>Что показывать на аватарке</h2>
            <button type="button" className="close" onClick={onClose} aria-label="Закрыть">
              <IconX size={18} stroke={1.8} />
            </button>
          </div>

          <div className="cropbox" ref={boxRef}
               onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp}>
            <img src={src} alt="" draggable={false} />
            {!tiny && (
              <>
                {/* Затемняем всё вокруг выбранного: так сразу видно, что
                    попадёт в аватарку, а что нет. */}
                <div className="cropmask" style={{
                  clipPath: `polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 0,
                              ${x1}% ${y1}%, ${x1}% ${y2}%, ${x2}% ${y2}%,
                              ${x2}% ${y1}%, ${x1}% ${y1}%)`,
                }} />
                <div className="cropsel" style={{
                  left: `${x1}%`, top: `${y1}%`, width: `${x2 - x1}%`, height: `${y2 - y1}%`,
                }} />
              </>
            )}
          </div>

          <span className="hint">
            Обведите лицо мышью — область всегда квадратная, как и сама
            аватарка. Хранится она в процентах от снимка, поэтому переживёт
            замену файла на скан покрупнее и видна в штатном интерфейсе.
            {drag ? '' : ' Можно обвести заново в любой момент.'}
          </span>

          <div className="actions">
            <button type="button" className="btn" onClick={() => onDone(null)}>
              Без обрезки
            </button>
            <div className="spacer" />
            <button type="button" className="btn" onClick={onClose}>Отмена</button>
            <button type="button" className="btn primary" disabled={tiny}
                    onClick={() => onDone([Math.round(x1), Math.round(y1), Math.round(x2), Math.round(y2)])}>
              Сохранить
            </button>
          </div>
        </div>
      </div>
    </Overlay>
  )
}
